class ProductCardVariant extends HTMLElement {
  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle;
    this.variantData = null;
    this.currentVariant = null;
    
    this.init();
  }

  async init() {
    await this.fetchProductData();
    this.attachEventListeners();
    this.updateAvailableOptions(this.getSelectedOptions());
    this.initScrollNavigation();
  }

  initScrollNavigation() {
    const scrollContainers = this.querySelectorAll('.product-card-variant__option-scroll-container');
    
    scrollContainers.forEach((container, index) => {
      const optionGroup = container.querySelector('.product-card-variant__option-group');
      const prevBtn = container.querySelector('.product-card-variant__option-nav--prev');
      const nextBtn = container.querySelector('.product-card-variant__option-nav--next');
      
      if (!optionGroup || !prevBtn || !nextBtn) return;

      // Check if scrolling is needed
      const updateNavigationState = () => {
        const hasScroll = optionGroup.scrollWidth > optionGroup.clientWidth;
        const isAtStart = optionGroup.scrollLeft <= 0;
        const isAtEnd = optionGroup.scrollLeft + optionGroup.clientWidth >= optionGroup.scrollWidth - 1;

        if (!hasScroll) {
          prevBtn.style.display = 'none';
          nextBtn.style.display = 'none';
        } else {
          prevBtn.style.display = 'flex';
          nextBtn.style.display = 'flex';
          prevBtn.disabled = isAtStart;
          nextBtn.disabled = isAtEnd;
        }
      };

      // Scroll handlers
      prevBtn.addEventListener('click', () => {
        optionGroup.scrollBy({ left: -100, behavior: 'smooth' });
        setTimeout(updateNavigationState, 300);
      });

      nextBtn.addEventListener('click', () => {
        optionGroup.scrollBy({ left: 100, behavior: 'smooth' });
        setTimeout(updateNavigationState, 300);
      });

      optionGroup.addEventListener('scroll', updateNavigationState);
      
      // Initial state
      updateNavigationState();
      
      // Update on window resize
      window.addEventListener('resize', updateNavigationState);
    });
  }

  async fetchProductData() {
    try {
      const response = await fetch(`/products/${this.productHandle}.js`);
      this.variantData = await response.json();
      this.currentVariant = this.variantData.variants.find(v => 
        v.id == this.querySelector('.product-card-variant__add-btn').dataset.variantId
      );
    } catch (error) {
      console.error('Error fetching product data:', error);
    }
  }

  attachEventListeners() {
    // Variant option buttons
    const optionButtons = this.querySelectorAll('.product-card-variant__option-btn');
    optionButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleVariantChange(e));
    });

    // Add to cart button
    const addButton = this.querySelector('.product-card-variant__add-btn');
    if (addButton) {
      addButton.addEventListener('click', () => this.handleAddToCart());
    }
  }

  handleVariantChange(event) {
    const button = event.currentTarget;
    if (button.classList.contains('disabled')) return;

    const optionPosition = parseInt(button.dataset.optionPosition);
    const value = button.dataset.value;

    // Update active state in the same option group
    const optionGroup = button.closest('.product-card-variant__option-group');
    optionGroup.querySelectorAll('.product-card-variant__option-btn').forEach(btn => {
      btn.classList.remove('active');
    });
    button.classList.add('active');

    // Get all selected options
    const selectedOptions = this.getSelectedOptions();

    // Find matching variant
    const matchingVariant = this.findMatchingVariant(selectedOptions);

    if (matchingVariant) {
      this.currentVariant = matchingVariant;
      this.updateAddToCartButton(matchingVariant);
      this.updateAvailableOptions(selectedOptions);
    }
  }

  getSelectedOptions() {
    const options = [];
    const optionGroups = this.querySelectorAll('.product-card-variant__option-group');
    
    optionGroups.forEach(group => {
      const activeBtn = group.querySelector('.product-card-variant__option-btn.active');
      if (activeBtn) {
        options.push(activeBtn.dataset.value);
      }
    });

    return options;
  }

  findMatchingVariant(selectedOptions) {
    return this.variantData.variants.find(variant => {
      return selectedOptions.every((option, index) => {
        return variant[`option${index + 1}`] === option;
      });
    });
  }

  updateAddToCartButton(variant) {
    const button = this.querySelector('.product-card-variant__add-btn');
    button.dataset.variantId = variant.id;
    
    if (variant.available) {
      button.disabled = false;
    } else {
      button.disabled = true;
    }
  }

  updateAvailableOptions(currentSelections) {
    if (!this.variantData) return;
    
    const optionGroups = this.querySelectorAll('.product-card-variant__option-group');
    
    optionGroups.forEach((group, groupIndex) => {
      const buttons = group.querySelectorAll('.product-card-variant__option-btn');
      
      buttons.forEach(button => {
        const testOptions = [...currentSelections];
        testOptions[groupIndex] = button.dataset.value;
        
        // Check if there's any available variant with this option value
        const hasAvailableVariant = this.variantData.variants.some(variant => {
          if (!variant.available) return false;
          
          // Match the option at this position
          const optionKey = `option${groupIndex + 1}`;
          if (variant[optionKey] !== button.dataset.value) return false;
          
          // Check if other selected options match (if any)
          return testOptions.every((option, index) => {
            if (index === groupIndex) return true; // Skip current option
            if (!option) return true; // No selection yet
            return variant[`option${index + 1}`] === option;
          });
        });

        if (hasAvailableVariant) {
          button.classList.remove('disabled');
          button.disabled = false;
        } else {
          button.classList.add('disabled');
          button.disabled = true;
        }
      });
    });
  }

  async handleAddToCart() {
    const button = this.querySelector('.product-card-variant__add-btn');
    const loadingOverlay = this.querySelector('.loading-overlay');
    const variantId = button.dataset.variantId;

    if (!variantId || button.disabled) return;

    // Show loading state
    button.classList.add('loading');
    loadingOverlay.classList.add('active');

    try {
      const formData = {
        items: [{
          id: variantId,
          quantity: 1
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const data = await response.json();

      // Update cart drawer
      this.updateCartDrawer();

      // Optional: Show success feedback
      this.showSuccessFeedback();

    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add product to cart. Please try again.');
    } finally {
      // Remove loading state
      button.classList.remove('loading');
      loadingOverlay.classList.remove('active');
    }
  }

  async updateCartDrawer() {
    // Fetch updated cart HTML
    try {
      const response = await fetch('/?section_id=cart-drawer');
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newCartDrawer = doc.querySelector('#Cart-Drawer');
      
      if (newCartDrawer) {
        const currentCartDrawer = document.querySelector('#Cart-Drawer');
        if (currentCartDrawer) {
          currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
        }
      }

      // Dispatch cart update event
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
      // Update cart count if it exists
      const cartCount = document.querySelector('.header-cart-count');
      if (cartCount) {
        const countResponse = await fetch('/cart.js');
        const cartData = await countResponse.json();
        cartCount.textContent = cartData.item_count;
      }
    } catch (error) {
      console.error('Error updating cart drawer:', error);
    }
  }

  showSuccessFeedback() {
    const button = this.querySelector('.product-card-variant__add-btn');
    button.style.background = '#22c55e';
    
    setTimeout(() => {
      button.style.background = '';
    }, 1000);
  }
}

customElements.define('product-card-variant', ProductCardVariant);
